/**
 * RepertoirePlayContainerOSMD
 *
 * React component that hosts the PhaserGame for OSMD-based repertoire rendering.
 * Parallels RepertoirePlayContainer (SVG version) but replaces the SVG Staff component
 * with a Phaser scene (OSMDRepertoireScene) that renders MusicXML directly.
 *
 * Responsibilities:
 * - Mount PhaserGame and start OSMDRepertoireScene
 * - Send phrase data to the scene via AddOSMDPhrase events
 * - Forward MIDI events to Phaser for NoteContainer visual feedback
 * - Forward play/pause state changes to Phaser
 * - Listen for RepertoireCompleted / RepertoirePhraseCompleted from Phaser
 * - Dispatch backend tracking events (play, pause, playing, completed, phrase completed)
 * - Forward soft-refresh to Phaser when updateKey changes
 */

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Phaser from 'phaser';
import { useDispatch, useSelector } from 'react-redux';
import { useWakeLock } from 'Hooks/useWakeLock';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { find } from 'lodash';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';

import { PhaserGame } from 'Phaser/PhaserGame';
import type { IRefPhaserGame } from 'Phaser/PhaserGame';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { TimingOffsetsConfig } from 'Models/EventStream';
import Phrase from 'Models/Phrase';
import { useRepertoirePlayContext } from 'Contexts/RepertoirePlayContext';
import {
  NotifyPhaser,
  AddOSMDPhrase,
  StartExercise,
  PauseExercise,
  ChangeTempo,
  SoftRefreshRepertoire as SoftRefreshRepertoireEvent,
  ListenForPhaserEvent,
  ListenPhaserReady,
  ListenStartedPlaying,
  ListenRepertoireCompleted,
  ListenRepertoirePhraseCompleted,
  ListenExercisePaused,
  StartPlayingArgs,
  ExerciseToReactEventType,
  RemoveListener,
  ResizeWindow,
  SetCursorVisible,
} from 'Phaser/EventBus';
import { RepertoireReducer, REPERTOIRE_PROGRESS } from 'Types/RepertoireTypes';
import { MainAppReducer, ErrorRecognitionActive } from 'Types';
import * as eventActions from 'Actions/events';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { IntervalTimer } from 'Utils/IntervalTimer';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import { debounce } from 'lodash';
import { getNamesConcatted } from 'Utils';
import * as repertoireActions from 'Actions/repertoire';
import type { AppDispatch } from 'Reducers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RepertoirePlayContainerOSMDProps {
  showKeyboard: boolean;
  setAccuracy: (accuracy: number) => void;
  setShowAccuracy: (show: boolean) => void;
  cursorIsVisible: boolean;
  scrollPosition?: number | null;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number | null>>;
}

// How often (ms) to dispatch the repertoirePlayingEventAction
const PLAY_TIME_SEND_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RepertoirePlayContainerOSMD: React.FC<RepertoirePlayContainerOSMDProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  useWakeLock();

  // ---- Analytics ----
  useEffect(() => {
    awsRum?.recordPageView(location.pathname);
    ReactGA?.send({ hitType: 'pageview', page: location.pathname });
  }, [location.pathname]);

  // ---- Context ----
  const {
    midiStream,
    phraseParts,
    allPhrasesLoaded,
    musicLoaded,
    setMusicLoaded,
    performanceTempo,
    updateKey,
    setDeferSchedulerStart,
    startScheduler,
    setForwardMidiToPhaser,
    setSkipContextEventDispatching,
    localPlaySessionId,
    scheduler2,
  } = useRepertoirePlayContext();

  // ---- Redux ----
  const appData = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const errorRecognitionActive: ErrorRecognitionActive = appData.errorRecognitionActive;
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const repertoirePlaying = repertoireReducerData.repertoirePlaying;
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const repertoireData = repertoireReducerData.repertoireData;
  const cursorVisible = repertoireReducerData.cursorVisible;

  // Derive tempo the same way RepertoirePlayContext does
  const repertoireSelected = useMemo(
    () => find(repertoireData, (rd) => rd.id === repertoireIdSelect),
    [repertoireData, repertoireIdSelect],
  );
  const repertoireProgressSelected = useMemo(
    () => find(userRepertoireProgress, (urd) => urd?.repertoire?.id === repertoireIdSelect),
    [userRepertoireProgress, repertoireIdSelect],
  );
  const currentTempo = repertoireProgressSelected?.last_tempo || performanceTempo || 72;

  // Use the context's localPlaySessionId — the context manages the play
  // session lifecycle and actively clears Redux's playSessionId to null,
  // making the Redux value unreliable for the OSMD container.
  const playSessionId = localPlaySessionId;

  // MIDI forwarding is now handled by the RepertoirePlayContext via
  // forwardMidiToPhaser flag — no separate MidiContext listener needed.

  // ---- Refs ----
  const phaserRef = useRef<IRefPhaserGame | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const phrasesSentRef = useRef(false);
  /** Latest repertoireSelected from Redux, stored in a ref so loadScene can
   *  read it without adding it to useCallback deps (which would destabilize
   *  the callback and risk missing the one-time PhaserReady event). */
  const repertoireSelectedRef = useRef(repertoireSelected);
  repertoireSelectedRef.current = repertoireSelected;
  /** Always-current repertoireIdSelect, readable from interval/timeout callbacks
   *  whose closures captured a stale value at effect registration time. */
  const repertoireIdSelectRef = useRef(repertoireIdSelect);
  repertoireIdSelectRef.current = repertoireIdSelect;
  /** Always-current playSessionId, readable from interval/timeout callbacks. */
  const playSessionIdRef = useRef(playSessionId);
  playSessionIdRef.current = playSessionId;
  /** Always-current cursorVisible, readable from callbacks that might
   *  capture a stale closure value (e.g. loadScene). */
  const cursorVisibleRef = useRef(cursorVisible);
  cursorVisibleRef.current = cursorVisible;
  /** The user's explicit cursor preference, updated only by user toggles.
   *  Lifecycle dispatches from the context (startScheduler → true,
   *  softRefreshRepertoire → false) are detected and filtered out so
   *  this ref always reflects the user's last deliberate choice. */
  const userCursorPrefRef = useRef(cursorVisible);
  /** When true, the next cursorVisible Redux change is a lifecycle dispatch
   *  (e.g. from startScheduler) and should NOT update userCursorPrefRef. */
  const cursorSyncSuppressedRef = useRef(false);
  const sceneReadyRef = useRef(false);

  useEffect(() => {
    logger.info('[CURSOR-VIS] Container mount/initial', {
      cursorVisible,
      userCursorPrefRef: userCursorPrefRef.current,
      cursorVisibleRef: cursorVisibleRef.current,
    });
  }, []);
  const prevUpdateKeyRef = useRef(updateKey);
  const sendPlayTimeIntervalRef = useRef<IntervalTimer | undefined>(undefined);
  const accumulatedTimeRef = useRef(0);
  const lastMeasuredTimeRef = useRef(0);
  const isFirstPlayPauseRef = useRef(true);
  /** Whether the first MIDI note has been played (count-in completed).
   *  Used to distinguish first-play (deferred scheduler) from resume
   *  (scheduler must be recalibrated before the scene starts updating). */
  const hasPlayedOnceRef = useRef(false);
  /** The global measure the cursor was repositioned to on pause.
   *  Used on resume to pass as measureOffset to startScheduler() so the
   *  TimeKeeper starts from exactly where the cursor was placed. */
  const pausedAtMeasureRef = useRef<number | undefined>(undefined);
  /** The actual OSMD global timestamp where the cursor was positioned on pause.
   *  Used to compute the correct TimeKeeper startMeasure on resume, since
   *  the raw measure index * measureDurationAt(0) may not match the actual
   *  OSMD timestamp (especially in non-4/4 time signatures). */
  const pausedAtTimestampRef = useRef<number | undefined>(undefined);
  const eventStreamUpdateLoopStartedRef = useRef(false);
  const accuracyLoopStartedRef = useRef(false);

  // ---- Defer scheduler start for OSMD ----
  // Tell the shared RepertoirePlayContext to skip scheduler2.unpauseTicks()
  // in onStart(). The scheduler will instead be started by startScheduler()
  // when the Phaser scene emits StartedPlaying (on the first MIDI note).
  useEffect(() => {
    setDeferSchedulerStart(true);
    return () => {
      setDeferSchedulerStart(false);
    };
  }, [setDeferSchedulerStart]);

  // ---- Skip context event dispatching in OSMD mode ----
  // The context's sendPlayTimeInterval and onCalcTheEnd dispatch
  // RepertoirePlayingEvent and RepertoireCompletedEvent using midiStream,
  // which has zero events in OSMD mode (events are in osmdEventStream).
  // This container handles its own event dispatching using osmdEventStream,
  // so tell the context to skip its duplicate dispatches.
  useEffect(() => {
    setSkipContextEventDispatching(true);
    return () => {
      setSkipContextEventDispatching(false);
    };
  }, [setSkipContextEventDispatching]);

  // ---- Forward MIDI to Phaser ----
  // Tell the shared RepertoirePlayContext to forward MIDI events to the
  // Phaser EventBus via NotifyPhaser(MidiOn/MidiOff). This lives in the
  // context's own MIDI listener (which survives clearListeners()) rather
  // than a separate listener that gets wiped by clearListeners().
  useEffect(() => {
    logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = true');
    setForwardMidiToPhaser(true);
    return () => {
      logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = false (unmount)');
      setForwardMidiToPhaser(false);
    };
  }, [setForwardMidiToPhaser]);

  // ---- Dimensions ----
  // Stable game dimensions passed to PhaserGame — set once and never updated.
  // PhaserGame's useLayoutEffect depends on [ref, height, width]; if these
  // change, the entire Phaser game is destroyed and recreated, wiping all
  // phrases, cursors, and event listeners. All subsequent resizing is handled
  // by the scene's resize() method via the ResizeWindow event bus event.
  const [gameWidth] = useState(() => Math.max(window.innerWidth, 960));
  // The header and footer are position:fixed overlays, so the canvas can fill
  // the full viewport height. Content behind the header/footer is handled by
  // the scene's top padding (for the toolbar) and is scrollable.
  const [gameHeight] = useState(() => Math.max(400, window.innerHeight));

  // Responsive wrapper height for the container div CSS — tracks available
  // vertical space so the Phaser canvas wrapper grows/shrinks with the window.
  // This state does NOT flow into PhaserGame props, so updating it won't
  // trigger game destruction.
  const [wrapperHeight, setWrapperHeight] = useState(() => Math.max(400, window.innerHeight));

  // ---- OSMDEventStream for Phaser scene ----
  // Uses the SAME timeKeeper as the context's midiStream / scheduler2.
  // midiStream.timeKeeper is the context's single TimeKeeper instance,
  // always defined at mount time (unlike scheduler2 which starts null).
  const [osmdEventStream] = useState(() => {
    const stream = new OSMDEventStream(
      midiStream.timeKeeper,
      new TimingOffsetsConfig(),
      appData.userData?.colorblind_type || null,
      1,
      0.025,
      0.1,
      true,
    );
    logger.debug('[OSMD-MIDI] Container: created osmdEventStream', {
      osmdEventStreamId: stream.id,
      contextMidiStreamId: midiStream.id,
      sharedTimeKeeper: '(same object ref from midiStream.timeKeeper)',
    });
    return stream;
  });

  // ---- Phrases ----
  const phrases = useMemo((): Phrase[] => {
    if (allPhrasesLoaded) {
      const partsArray: Array<Phrase | undefined> = phraseParts.toArray();
      return partsArray.filter((phrase): phrase is Phrase => phrase !== undefined);
    }
    return [];
  }, [phraseParts, allPhrasesLoaded]);

  // Mark music as loaded when all phrases are ready
  useEffect(() => {
    if (allPhrasesLoaded) {
      setMusicLoaded(true);
      // Diagnostic: log both event stream states when phrases are loaded
      logger.info('[ACC-DBG] allPhrasesLoaded — event stream state', {
        osmdEventStreamId: osmdEventStream.id,
        osmdAccuracyQueueLength: osmdEventStream.accuracyQueueLength(),
        osmdTotalNotes: osmdEventStream.totalNotesLength(),
        contextMidiStreamId: midiStream.id,
        contextMidiAccuracyQueueLength: midiStream.accuracyQueueLength(),
        contextMidiTotalNotes: midiStream.totalNotesLength(),
      });
    }
  }, [allPhrasesLoaded, setMusicLoaded, osmdEventStream, midiStream]);

  // BPM sync is handled by the context (RepertoirePlayContext line 865:
  // timeKeeper.setBpm(tempo)). Since osmdEventStream shares the same
  // timeKeeper via midiStream.timeKeeper, no additional sync is needed here.

  // ---- Sync error recognition (hand separation) to osmdEventStream ----
  // The context syncs errorRecognitionActive to midiStream (line 324), but in
  // OSMD mode midiStream has no events — osmdEventStream is the one that
  // processes MIDI input, colors notes, and calculates accuracy. Without this
  // sync, the hand toggle in the UI has no effect on note matching or coloring.
  useEffect(() => {
    osmdEventStream.setErrorRecognitionActive(errorRecognitionActive);
    logger.debug('[OSMD-MIDI] Container: synced errorRecognitionActive to osmdEventStream', {
      left: errorRecognitionActive.left,
      right: errorRecognitionActive.right,
    });
  }, [errorRecognitionActive, osmdEventStream]);

  // ---- Sync cursor visibility to Phaser scene ----
  // The settings toggle updates rep.cursorVisible in Redux. Forward this to
  // the Phaser scene so it can show/hide cursor graphics without affecting
  // the cursor's internal position tracking.
  //
  // The shared context also dispatches setCursorVisible during lifecycle
  // transitions (softRefreshRepertoire dispatches false, startScheduler
  // dispatches true). These are NOT user preference changes:
  //  - softRefreshRepertoire changes are detected by updateKey also changing
  //    in the same render cycle.
  //  - startScheduler changes are detected by cursorSyncSuppressedRef.
  useEffect(() => {
    const isSoftRefresh = updateKey !== prevUpdateKeyRef.current;
    const isSuppressed = cursorSyncSuppressedRef.current;
    // Only update the user preference ref for genuine user toggles.
    if (!isSoftRefresh && !isSuppressed) {
      userCursorPrefRef.current = cursorVisible;
    }
    if (!sceneReadyRef.current) return;
    logger.info('[CURSOR-VIS] Container useEffect: sending SetCursorVisible', {
      cursorVisible,
      isSoftRefresh,
      isSuppressed,
    });
    NotifyPhaser(SetCursorVisible(cursorVisible));
  }, [cursorVisible, updateKey]);

  // ---- Event Stream Update Loop ----
  // The osmdEventStream needs update() called periodically to detect missed
  // notes and color them red. This mirrors how MainAppPhaser calls
  // midiStream.update() via scheduler2.setIntervalByTick().
  const startEventStreamUpdateLoop = useCallback(() => {
    if (eventStreamUpdateLoopStartedRef.current || !scheduler2) {
      return;
    }
    eventStreamUpdateLoopStartedRef.current = true;
    logger.debug('[OSMD-MIDI] Container: starting osmdEventStream update loop');
    let updateLoopCallCount = 0;
    scheduler2.setIntervalByTick(
      'updateOsmdEventStream',
      () => {
        osmdEventStream.update();
        updateLoopCallCount++;
        // Log every 100 calls (~3 seconds at 32nd note resolution)
        if (updateLoopCallCount % 100 === 1) {
          logger.debug('[ACC-DBG] eventStream.update() periodic check', {
            callCount: updateLoopCallCount,
            accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
            totalNotes: osmdEventStream.totalNotesLength(),
            osmdEventStreamId: osmdEventStream.id,
          });
        }
      },
      NoteType._32nd,
      1,
    );
  }, [scheduler2, osmdEventStream]);

  // ---- Accuracy Update Loop ----
  // The context's startAccuracyLoop reads from midiStream, which has no
  // events in OSMD mode (events are added to osmdEventStream in the scene).
  // This loop uses osmdEventStream instead and calls props.setAccuracy()
  // to drive the UI accuracy display. The context's loop will get NaN from
  // midiStream.calcRepAccuracy() and skip the setAccuracy call (isNaN guard),
  // so these two loops do not conflict.
  const startAccuracyUpdateLoop = useCallback(() => {
    if (accuracyLoopStartedRef.current || !scheduler2) {
      return;
    }
    accuracyLoopStartedRef.current = true;
    logger.debug('[OSMD-MIDI] Container: starting accuracy update loop using osmdEventStream');
    scheduler2.setIntervalByTick(
      'osmdAccuracyLoop',
      () => {
        const [accuracy] = osmdEventStream.calcRepAccuracy(
          currentTempo,
          performanceTempo ?? currentTempo,
        );
        logger.debug('[ACC-DBG] accuracyLoop tick', {
          accuracy,
          isNaN: isNaN(accuracy),
          accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
          totalNotes: osmdEventStream.totalNotesLength(),
        });
        if (!isNaN(accuracy)) {
          props.setAccuracy(accuracy);
        }
      },
      NoteType.QUARTER,
      1,
    );
  }, [scheduler2, osmdEventStream, currentTempo, performanceTempo, props.setAccuracy]);

  // ---- Container sizing via ResizeObserver ----
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = debounce(() => {
      if (document.hidden) return;
      const { clientWidth, clientHeight } = container;
      if (clientWidth > 100 && clientHeight > 100) {
        // Only update the wrapper height (for CSS) — NOT the game dimensions.
        // Updating gameWidth/gameHeight would cause PhaserGame to destroy and
        // recreate the entire Phaser game, wiping all phrases and cursors.
        // The scene's resize() method handles canvas + layout resizing via
        // this.scale.resize() and layoutPhrases().
        setWrapperHeight(clientHeight);
        if (sceneReadyRef.current) {
          NotifyPhaser(ResizeWindow(Math.max(clientWidth, 960), clientHeight));
        }
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      handleResize.cancel();
    };
  }, []);

  // ---- Start Phaser scene on PhaserReady ----
  const loadScene = useCallback(() => {
    if (!phaserRef.current?.scene) return;
    logger.debug('[OSMD-MIDI] Container: starting OSMDRepertoireScene', {
      osmdEventStreamId: osmdEventStream.id,
      gameWidth,
      gameHeight,
    });
    // Read from ref so loadScene stays stable (no repertoireSelected in deps).
    // Adding repertoireSelected to deps would cause useCallback to produce a
    // new function reference on every Redux update, which re-registers the
    // PhaserReady listener and risks missing the one-time event.
    const selectedRepertoire = repertoireSelectedRef.current;
    const repertoireTitle = selectedRepertoire?.name ?? '';
    const composerNames = selectedRepertoire?.composers
      ? getNamesConcatted(selectedRepertoire.composers)
      : '';
    logger.debug('[OSMD-MIDI] loadScene title/composer data', {
      hasSelectedRepertoire: !!selectedRepertoire,
      repertoireTitle,
      composerNames,
    });
    // Use the current Redux value (cursorVisibleRef is updated every render).
    // Pass to scene so init() has it before collectWaiting AddPhrase triggers createCursorForPhrase.
    const prefToSend = cursorVisibleRef.current;
    phaserRef.current.scene.scene.start('OSMDRepertoireScene', {
      eventStream: osmdEventStream,
      width: gameWidth,
      height: gameHeight,
      performanceTempo: performanceTempo ?? 72,
      title: repertoireTitle,
      composerName: composerNames,
      cursorVisible: prefToSend,
    });
    sceneReadyRef.current = true;
    logger.info('[CURSOR-VIS] loadScene: sending SetCursorVisible', {
      cursorVisibleRef: prefToSend,
      userCursorPrefRef: userCursorPrefRef.current,
    });
    NotifyPhaser(SetCursorVisible(prefToSend));
  }, [osmdEventStream, gameWidth, gameHeight]);

  useEffect(() => {
    ListenForPhaserEvent(ListenPhaserReady(loadScene));
    return () => {
      RemoveListener(ExerciseToReactEventType.PhaserReady);
    };
  }, [loadScene]);

  // ---- Send phrases to scene when loaded ----
  useEffect(() => {
    if (allPhrasesLoaded && phrases.length > 0 && !phrasesSentRef.current) {
      phrasesSentRef.current = true;
      logger.info('[ACC-DBG] sending phrases to scene', {
        phraseCount: phrases.length,
        osmdEventStreamId: osmdEventStream.id,
        osmdTotalNotesBefore: osmdEventStream.totalNotesLength(),
        osmdAccuracyQueueLengthBefore: osmdEventStream.accuracyQueueLength(),
        contextMidiStreamId: midiStream.id,
        contextMidiTotalNotes: midiStream.totalNotesLength(),
      });
      phrases.forEach((phrase, index) => {
        NotifyPhaser(
          AddOSMDPhrase(
            phrase,
            /* showClef */ true,
            /* showBrace */ true,
            /* showTimeSig */ index === 0,
          ),
        );
      });
      // Log after sending — events are added asynchronously by the scene,
      // so totalNotesLength may not reflect the new events immediately.
      logger.info('[ACC-DBG] phrases sent — immediate post-send check', {
        osmdTotalNotesAfter: osmdEventStream.totalNotesLength(),
        osmdAccuracyQueueLengthAfter: osmdEventStream.accuracyQueueLength(),
      });
    }
  }, [allPhrasesLoaded, phrases]);

  // ---- Forward tempo changes to Phaser ----
  // When the user adjusts the tempo slider, currentTempo (derived from Redux)
  // updates. Forward the change to the scene so the count-in metronome click
  // rate and cursor blink speed update in real time.
  /** Always-current tempo value, readable from callbacks whose closures
   *  don't include currentTempo in their dependency arrays (e.g. the
   *  ListenStartedPlaying handler). */
  const currentTempoRef = useRef(currentTempo);
  currentTempoRef.current = currentTempo;

  const prevTempoRef = useRef(currentTempo);
  useEffect(() => {
    if (currentTempo !== prevTempoRef.current) {
      prevTempoRef.current = currentTempo;
      NotifyPhaser(ChangeTempo(currentTempo));
    }
  }, [currentTempo]);

  // MIDI forwarding to Phaser is now handled by the context's own listener
  // via the forwardMidiToPhaser flag (set above). This ensures MIDI events
  // survive clearListeners() calls in RepertoirePlayContext.init().

  // ---- Play / Pause forwarding to Phaser + event dispatching ----
  useEffect(() => {
    // Skip the very first render to avoid spurious events
    if (isFirstPlayPauseRef.current) {
      isFirstPlayPauseRef.current = false;
      return;
    }

    if (repertoirePlaying) {
      // On resume (after the first MIDI note was already played), recalibrate
      // the shared TimeKeeper BEFORE telling the scene to start updating.
      // Without this, the scene's update() loop reads stale timestamps —
      // TimeKeeper.audioTimeToMeasureTimestamp() uses startTime from the
      // original first play against the ever-advancing Tone.context.currentTime,
      // producing an enormous timestamp that races the cursor to the end.
      // On first play, the deferred pattern is correct: the scene counts in,
      // and startScheduler() is called on the first MIDI note via StartedPlaying.
      if (hasPlayedOnceRef.current) {
        // On resume, bypass startScheduler() which relies on deferred args
        // that may not be available (the context's onStart() stores them,
        // but the effect ordering can cause startScheduler to read null).
        // Instead, directly unpause the scheduler/TimeKeeper here, which is
        // what startScheduler ultimately does anyway.
        // Compute the TimeKeeper's startMeasure from the actual OSMD
        // timestamp rather than the raw measure index. The TimeKeeper's
        // audioTimeToMeasureTimestamp() formula is:
        //   (startMeasure + timeOffset/measureDurationSecs) * measureDurationAt(0)
        // At timeOffset=0 the result is startMeasure * measureDurationAt(0).
        // We want the initial result to equal the OSMD timestamp where the
        // cursor was positioned on pause. So:
        //   startMeasure = pausedAtTimestamp / measureDurationAt(0)
        // This corrects for non-4/4 time signatures where the raw measure
        // index multiplied by the time-sig fraction doesn't match the actual
        // OSMD timestamp.
        const measureDuration = midiStream.timeKeeper.measureDurationAt(0);
        const pausedTimestamp = pausedAtTimestampRef.current;
        const fallbackMeasure = pausedAtMeasureRef.current ?? 0;
        const resumeMeasure =
          pausedTimestamp !== undefined && measureDuration > 0
            ? pausedTimestamp / measureDuration
            : fallbackMeasure;
        if (scheduler2) {
          scheduler2.lastPauseMeasure = resumeMeasure;
          // On the native path unpauseTicks returns a Promise; await it so
          // the native scheduler finishes and TimeKeeper is unpaused before
          // the scene starts processing notes on resume.
          const maybePromise = scheduler2.unpauseTicks(
            currentTempo,
            1,
            resumeMeasure,
            NoteType._64th,
          );
          if (maybePromise) {
            maybePromise.then(() => NotifyPhaser(StartExercise(currentTempo)));
          } else {
            NotifyPhaser(StartExercise(currentTempo));
          }
        } else {
          NotifyPhaser(StartExercise(currentTempo));
        }
      } else {
        NotifyPhaser(StartExercise(currentTempo));
      }

      // Dispatch play event to backend (use refs for latest Redux values)
      if (repertoireIdSelectRef.current !== undefined && playSessionIdRef.current) {
        dispatch(
          eventActions.repertoirePlayEventAction(
            repertoireIdSelectRef.current,
            playSessionIdRef.current,
          ) as ReturnType<typeof eventActions.repertoirePlayEventAction>,
        );
      }

      // Start the 10-second play time reporting interval
      lastMeasuredTimeRef.current = Date.now();
      accumulatedTimeRef.current = 0;

      sendPlayTimeIntervalRef.current = new IntervalTimer(() => {
        // Read from refs to get the latest Redux values — the effect closure
        // captures stale values because its dependency array is [repertoirePlaying].
        const latestRepertoireId = repertoireIdSelectRef.current;
        const latestPlaySessionId = playSessionIdRef.current;
        const latestTempo = currentTempoRef.current;

        if (latestRepertoireId !== undefined && latestPlaySessionId) {
          const now = Date.now();
          accumulatedTimeRef.current += now - lastMeasuredTimeRef.current;
          lastMeasuredTimeRef.current = now;

          const [rawAccuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
            latestTempo,
            performanceTempo ?? latestTempo,
          );
          // calcRepAccuracy returns NaN when the accuracy queue is empty
          // (no notes matched yet). NaN serializes to null in JSON, which
          // the backend rejects. Default to 0 until notes are marked.
          const playingAccuracy = isNaN(rawAccuracy) ? 0 : rawAccuracy;
          const totalNotesCount = osmdEventStream.totalNotesLength();
          let completionSum = 0;
          accuracyAtTempo.forEach((value: number) => (completionSum += value));
          const percentComplete = totalNotesCount > 0 ? (completionSum / totalNotesCount) * 100 : 0;

          logger.info('[ACC-TRACE] CONTAINER RepertoirePlayingEvent dispatch', {
            source: 'container-interval',
            rawAccuracy,
            playingAccuracy,
            isNaN_rawAccuracy: isNaN(rawAccuracy),
            typeof_rawAccuracy: typeof rawAccuracy,
            accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
            totalNotesCount,
            percentComplete,
            accumulatedTime: accumulatedTimeRef.current,
            latestTempo,
            performanceTempo,
            latestRepertoireId,
            latestPlaySessionId,
            osmdEventStreamId: osmdEventStream.id,
            valueSentToBackend: playingAccuracy,
          });

          dispatch(
            eventActions.repertoirePlayingEventAction(
              latestRepertoireId,
              latestPlaySessionId,
              accumulatedTimeRef.current,
              percentComplete,
              playingAccuracy,
            ) as ReturnType<typeof eventActions.repertoirePlayingEventAction>,
          );
        } else {
          logger.warn('[ACC-DBG] CONTAINER IntervalTimer skipped — missing IDs', {
            latestRepertoireId,
            latestPlaySessionId,
          });
        }
      }, PLAY_TIME_SEND_INTERVAL_MS);
      sendPlayTimeIntervalRef.current.start();
    } else {
      NotifyPhaser(PauseExercise());

      // Dispatch pause event to backend (use refs for latest Redux values)
      if (repertoireIdSelectRef.current !== undefined && playSessionIdRef.current) {
        dispatch(
          eventActions.repertoirePauseEventAction(
            repertoireIdSelectRef.current,
            playSessionIdRef.current,
          ) as ReturnType<typeof eventActions.repertoirePauseEventAction>,
        );
      }

      // Stop the play time interval
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
    }
  }, [repertoirePlaying]);

  // ---- Listen for StartedPlaying from Phaser ----
  // When the user plays the first note during count-in, the Phaser scene
  // emits StartedPlaying with a processEventsQueue callback. We must
  // invoke that callback to flush the queued first-note MIDI event
  // through the event stream so it gets matched and colored.
  // We also call startScheduler() here to perform the deferred
  // scheduler2.unpauseTicks(), mirroring how MainAppPhaser calls onStart
  // only when it receives StartedPlaying from the scene.
  useEffect(() => {
    ListenForPhaserEvent(
      ListenStartedPlaying(async (args?: StartPlayingArgs) => {
        logger.info('[ACC-DBG] StartedPlaying received — event stream state', {
          hasProcessEventsQueue: !!args?.processEventsQueue,
          measureOffset: args?.measureOffset,
          hasPlayedOnce: hasPlayedOnceRef.current,
          osmdEventStreamId: osmdEventStream.id,
          osmdTotalNotes: osmdEventStream.totalNotesLength(),
          osmdAccuracyQueueLength: osmdEventStream.accuracyQueueLength(),
          contextMidiStreamId: midiStream.id,
          contextMidiTotalNotes: midiStream.totalNotesLength(),
          contextMidiAccuracyQueueLength: midiStream.accuracyQueueLength(),
          timeKeeperPausedBefore: midiStream.timeKeeper.isPaused(),
        });

        // On first play only: start the shared TimeKeeper now that the
        // first MIDI note has been played during count-in.
        // On resume, startScheduler() was already called in the play/pause
        // effect (before StartExercise was sent to the scene), so the
        // TimeKeeper is already recalibrated — calling it again is a no-op
        // (deferredUnpauseArgs was already consumed).
        if (!hasPlayedOnceRef.current) {
          // Must await because startScheduler is async (calls scheduler2.unpauseTicks).
          // The TimeKeeper must be unpaused BEFORE processEventsQueue runs,
          // otherwise handleNoteOnEvent will early-return on isPaused().
          // Pass measureOffset so the scheduler's lastPauseMeasure is set
          // correctly, aligning TimeKeeper timestamps with event buckets.
          // Suppress user-preference tracking — startScheduler() dispatches
          // setCursorVisible(true) as a lifecycle action, not a user toggle.
          cursorSyncSuppressedRef.current = true;
          // Pass currentTempoRef as tempoOverride so startPlayback() receives
          // the user's latest BPM even if the context's tempo useEffect hasn't
          // flushed the slider change into deferredUnpauseArgsRef yet.
          const tempoAtStart = currentTempoRef.current;
          await startScheduler(args?.measureOffset, tempoAtStart);

          // The shared context's startScheduler() unconditionally dispatches
          // setCursorVisible(true) (designed for the SVG cursor hide/show
          // pattern). For the OSMD path the user's explicit preference must
          // be preserved, so re-dispatch the actual preference.
          const userCursorPref = userCursorPrefRef.current;
          dispatch(repertoireActions.setCursorVisible(userCursorPref));
          cursorSyncSuppressedRef.current = false;

          // Safety net: correct the TimeKeeper BPM if it still doesn't
          // match the user's latest tempo after startScheduler(). The primary
          // fix passes tempoOverride (tempoAtStart) to startScheduler() so
          // startPlayback() receives the correct BPM from the start, but we
          // keep this check in case of any residual mismatch.
          const latestTempo = currentTempoRef.current;
          if (midiStream.timeKeeper.getBpm() !== latestTempo) {
            logger.debug('[OSMD-MIDI] Container: correcting TimeKeeper BPM after startScheduler', {
              staleBpm: midiStream.timeKeeper.getBpm(),
              correctBpm: latestTempo,
            });
            midiStream.timeKeeper.setBpm(latestTempo);
          }

          hasPlayedOnceRef.current = true;
        }

        logger.debug('[OSMD-MIDI] Container: after startScheduler()', {
          timeKeeperPausedAfter: midiStream.timeKeeper.isPaused(),
        });
        // Start the event stream update loop now that the scheduler is running.
        // This calls osmdEventStream.update() periodically to detect missed
        // notes and color them red (NEVER_PLAYED state).
        startEventStreamUpdateLoop();
        // Start the accuracy update loop so the UI accuracy display reflects
        // notes matched in osmdEventStream (not the context's empty midiStream).
        startAccuracyUpdateLoop();
        if (args?.processEventsQueue) {
          const [processQueueFn, processQueueScope] = args.processEventsQueue;
          logger.debug('[OSMD-MIDI] Container: calling processEventsQueue NOW');
          processQueueFn.call(processQueueScope);
          logger.debug('[OSMD-MIDI] Container: processEventsQueue returned');
        }
      }),
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.StartedPlaying);
    };
  }, [
    dispatch,
    midiStream,
    osmdEventStream,
    startScheduler,
    startEventStreamUpdateLoop,
    startAccuracyUpdateLoop,
  ]);

  // ---- Listen for ExercisePaused from Phaser ----
  // When the scene pauses and repositions the cursor to the last completed
  // measure, it emits ExercisePaused(lastPlayedMeasure). We store this so
  // that on resume, startScheduler() can pass it as measureOffset, ensuring
  // the TimeKeeper starts from exactly where the cursor was placed.
  useEffect(() => {
    ListenForPhaserEvent(
      ListenExercisePaused(
        ({
          lastPlayedMeasure,
          pausedAtTimestamp,
        }: {
          lastPlayedMeasure?: number;
          pausedAtTimestamp?: number;
        }) => {
          logger.debug('[OSMD-MIDI] Container: ExercisePaused received', {
            lastPlayedMeasure,
            pausedAtTimestamp,
          });
          pausedAtMeasureRef.current = lastPlayedMeasure;
          pausedAtTimestampRef.current = pausedAtTimestamp;
        },
      ),
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.ExercisePaused);
    };
  }, []);

  // ---- Listen for Phaser completion events ----
  useEffect(() => {
    ListenForPhaserEvent(
      ListenRepertoireCompleted(() => {
        // Read from refs to get the latest Redux values — this handler is
        // registered once and its closure would otherwise capture stale values.
        const latestRepertoireId = repertoireIdSelectRef.current;
        const latestPlaySessionId = playSessionIdRef.current;
        const latestTempo = currentTempoRef.current;

        logger.info('RepertoirePlayContainerOSMD: RepertoireCompleted received from Phaser');
        if (latestRepertoireId !== undefined && latestPlaySessionId) {
          const [rawCompletedAccuracy, completedAccuracyAtTempo] = osmdEventStream.calcRepAccuracy(
            latestTempo,
            performanceTempo ?? latestTempo,
          );
          // calcRepAccuracy returns NaN when the accuracy queue is empty
          // (no notes matched yet). NaN serializes to null in JSON, which
          // the backend rejects. Default to 0 until notes are marked.
          const completedAccuracy = isNaN(rawCompletedAccuracy) ? 0 : rawCompletedAccuracy;
          const totalNotesCount = osmdEventStream.totalNotesLength();
          let completionSum = 0;
          completedAccuracyAtTempo.forEach((value: number) => (completionSum += value));
          const completion = totalNotesCount > 0 ? (completionSum / totalNotesCount) * 100 : 0;

          logger.info('[ACC-TRACE] CONTAINER RepertoireCompletedEvent dispatch', {
            source: 'container-completed',
            rawCompletedAccuracy,
            completedAccuracy,
            isNaN_rawAccuracy: isNaN(rawCompletedAccuracy),
            typeof_rawCompletedAccuracy: typeof rawCompletedAccuracy,
            accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
            totalNotesCount,
            completion,
            latestTempo,
            performanceTempo,
            latestRepertoireId,
            latestPlaySessionId,
            osmdEventStreamId: osmdEventStream.id,
            valueSentToBackend: completedAccuracy,
          });

          dispatch(
            eventActions.repertoireCompletedEventAction(
              latestRepertoireId,
              latestPlaySessionId,
              completion,
              completedAccuracy,
            ) as ReturnType<typeof eventActions.repertoireCompletedEventAction>,
          );
        } else {
          logger.warn('[ACC-DBG] CONTAINER RepertoireCompleted skipped — missing IDs', {
            latestRepertoireId,
            latestPlaySessionId,
          });
        }
      }),
    );

    ListenForPhaserEvent(
      ListenRepertoirePhraseCompleted(({ phraseIndex }: { phraseIndex: number }) => {
        const phrase = phrases[phraseIndex];
        // Read from refs for latest Redux values
        const latestPlaySessionId = playSessionIdRef.current;
        const latestRepertoireId = repertoireIdSelectRef.current;
        if (phrase && latestPlaySessionId && latestRepertoireId !== undefined) {
          // Delay dispatch by 2 seconds to capture the final note offset
          setTimeout(() => {
            if (phrase.getComplete()) return;
            phrase.setComplete(true);

            const phraseData = phrase.generateRepPhraseCompletedEventData();
            dispatch(
              eventActions.repPhraseCompletedEventAction({
                midiData: [],
                errorData: phraseData.errorData,
                accuracy: phraseData.accuracy,
                lHAccuracy: phraseData.lhAccuracy,
                rHAccuracy: phraseData.rhAccuracy,
                totalNotes: phraseData.totalNotes,
                lhTotalNotes: phraseData.lhTotalNotes,
                rhTotalNotes: phraseData.rhTotalNotes,
                errorScore: phraseData.errorScore,
                lhErrorScore: phraseData.lhErrorScore,
                rhErrorScore: phraseData.rhErrorScore,
                phraseS3Id: phrase.uuid,
                repertoireId: latestRepertoireId,
                bpm: phraseData.bpm,
                playSessionId: latestPlaySessionId,
              }) as ReturnType<typeof eventActions.repPhraseCompletedEventAction>,
            );
          }, 2000);
        }
      }),
    );

    return () => {
      RemoveListener(ExerciseToReactEventType.RepertoireCompleted);
      RemoveListener(ExerciseToReactEventType.RepertoirePhraseCompleted);
    };
  }, [
    phrases,
    playSessionId,
    repertoireIdSelect,
    currentTempo,
    performanceTempo,
    midiStream,
    dispatch,
    osmdEventStream,
  ]);

  // ---- Update user-repertoire-progress using osmdEventStream ----
  // In OSMD mode, the context's progress update (which uses midiStream with
  // VexFlow iterators) is skipped. This effect uses osmdEventStream (which
  // has the correct OSMD-based events) to send accurate percent_complete
  // and accuracy values when playback stops.
  const isFirstProgressUpdateRef = useRef(true);
  useEffect(() => {
    // Skip the initial render (musicLoaded may already be true on mount)
    if (isFirstProgressUpdateRef.current) {
      isFirstProgressUpdateRef.current = false;
      return;
    }
    if (!musicLoaded || repertoirePlaying) return;

    const latestRepertoireId = repertoireIdSelectRef.current;
    const latestTempo = currentTempoRef.current;

    const [rawAccuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
      latestTempo,
      performanceTempo ?? latestTempo,
    );
    const accuracy = isNaN(rawAccuracy) ? 0 : rawAccuracy;
    const totalNotesCount = osmdEventStream.totalNotesLength();
    let completionSum = 0;
    accuracyAtTempo.forEach((value: number) => (completionSum += value));
    const percentComplete = totalNotesCount > 0 ? completionSum / totalNotesCount : 0;

    let repertoireStatus = REPERTOIRE_PROGRESS.IN_PROGRESS;
    if (percentComplete >= 1.0) {
      repertoireStatus = REPERTOIRE_PROGRESS.COMPLETE;
    }

    // Compare with previous best values — never regress the user's progress
    let bestAccuracy: number;
    let bestPercentComplete: number;
    if (
      repertoireProgressSelected &&
      repertoireProgressSelected.percent_complete &&
      100 * percentComplete < repertoireProgressSelected.percent_complete
    ) {
      bestAccuracy = repertoireProgressSelected.accuracy ?? 0;
      bestPercentComplete = repertoireProgressSelected.percent_complete;
    } else {
      bestAccuracy = accuracy;
      bestPercentComplete = 100 * percentComplete;
    }

    const progressData = {
      status: repertoireStatus,
      repertoire: { id: latestRepertoireId },
      last_played_at: new Date().toISOString(),
      completed_at:
        repertoireStatus === REPERTOIRE_PROGRESS.COMPLETE ? new Date().toISOString() : null,
      accuracy: bestAccuracy,
      percent_complete: bestPercentComplete,
      last_tempo: latestTempo,
    };
    logger.info('[ACC-TRACE] CONTAINER progress update dispatch', progressData);
    dispatch(repertoireActions.updateRepertoireDataRequest({ ...progressData }));
  }, [musicLoaded, repertoirePlaying]);

  // ---- Forward soft-refresh to Phaser ----
  // RepertoirePlayContext.softRefreshRepertoire() increments updateKey.
  // When updateKey changes, we know a soft refresh occurred and notify the scene.
  useEffect(() => {
    if (updateKey !== prevUpdateKeyRef.current) {
      prevUpdateKeyRef.current = updateKey;
      logger.info('RepertoirePlayContainerOSMD: soft refresh detected, notifying Phaser');
      NotifyPhaser(SoftRefreshRepertoireEvent());

      // Reset phrase-sent flag so phrases can be re-sent if needed after refresh
      // (In practice, phrases are already loaded; the scene's softReset handles visuals)
      phrasesSentRef.current = true;

      // Reset the update loop flags so they can be restarted on next play
      eventStreamUpdateLoopStartedRef.current = false;
      accuracyLoopStartedRef.current = false;

      // Reset first-play tracking so the next play cycle uses the deferred
      // scheduler pattern (count-in → first MIDI note → startScheduler)
      hasPlayedOnceRef.current = false;

      // Clear the paused measure/timestamp — fresh play cycle after soft refresh
      pausedAtMeasureRef.current = undefined;
      pausedAtTimestampRef.current = undefined;

      // Auto-start: the scene's softReset already begins the count-in
      // (cursor blinking, auxiliary TimeKeeper running). Set repertoirePlaying
      // to true so the UI shows the pause button and the context's onStart()
      // stores the deferred scheduler args for when the first note is played.
      // The play/pause effect will send StartExercise, but the scene's
      // startExercise is idempotent when the count-in is already active.
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: true }));
      dispatch(repertoireActions.setRepertoireEnded(false));
    }
  }, [updateKey, dispatch]);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
      RemoveListener(ExerciseToReactEventType.PhaserReady);
      RemoveListener(ExerciseToReactEventType.StartedPlaying);
      RemoveListener(ExerciseToReactEventType.RepertoireCompleted);
      RemoveListener(ExerciseToReactEventType.RepertoirePhraseCompleted);
    };
  }, []);

  // ---- Render ----
  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100vw',
        flex: 1, // fill parent height (main container has flex: 1 in RepertoirePlay)
        minHeight: 0, // allow flex shrink below content size
      }}
    >
      {musicLoaded && allPhrasesLoaded ? (
        <div
          className="osmd-repertoire-game-wrapper"
          style={{
            width: '100%',
            height: `${wrapperHeight}px`,
            backgroundColor: 'rgb(235, 235, 235)',
          }}
        >
          {/*
            PhaserGame sets pointer-events: none on #game-container for the
            lesson section (where the game area shouldn't capture clicks).
            Repertoire needs pointer events for scroll input, so we re-enable
            them via an inline <style> scoped to this wrapper.
          */}
          <style>{`.osmd-repertoire-game-wrapper #game-container { pointer-events: auto !important; }`}</style>
          <PhaserGame
            width={gameWidth}
            height={gameHeight}
            ref={phaserRef}
            scaling={Phaser.Scale.NONE}
            crisp={true}
          />
        </div>
      ) : (
        <CustomCircularProgress />
      )}
    </div>
  );
};

export default RepertoirePlayContainerOSMD;
